import { Q } from "@nozbe/watermelondb";
import { database } from "..";
import SmsMessage from "../model/smsMessage";
import Customer from "../model/customer";
import { apiRequest } from "../utils";

export type APISms = {
  id: string;
  customerId: string;
  direction: "in" | "out";
  body: string;
  toNumber: string;
  fromNumber: string;
  status: "queued" | "sent" | "failed" | "received";
  error: string;
  createdAt: number;
};

function toAPI(m: SmsMessage): APISms {
  return {
    id: m.id,
    customerId: m.customer?.id || "",
    direction: m.direction === "in" ? "in" : "out",
    body: m.body || "",
    toNumber: m.toNumber || "",
    fromNumber: m.fromNumber || "",
    status: (m.status as APISms["status"]) || "queued",
    error: m.error || "",
    createdAt: Number(m.createdAt) || 0,
  };
}

export async function getConversation(customerId: string): Promise<APISms[]> {
  const messages = await database
    .get<SmsMessage>("sms_messages")
    .query(Q.where("customer_id", customerId))
    .fetch();
  return messages.map(toAPI).sort((a, b) => a.createdAt - b.createdAt);
}

export async function getAllSms(): Promise<APISms[]> {
  const messages = await database.get<SmsMessage>("sms_messages").query().fetch();
  return messages.map(toAPI);
}

// Saves the text on this device; it is sent by the server on the next sync,
// which starts automatically a moment later.
export async function sendSms(
  customerId: string,
  toNumber: string,
  body: string
): Promise<void> {
  const customer = await database.get<Customer>("customers").find(customerId);
  await database.write(async () => {
    await database.get<SmsMessage>("sms_messages").create((m) => {
      const c: any = m.customer;
      c.set(customer);
      m.direction = "out";
      m.body = body;
      m.toNumber = toNumber;
      m.fromNumber = "";
      m.status = "queued";
      m.error = "";
    });
  });
}

export type SmsStatus = {
  enabled: boolean;
  fromNumber?: string;
  repliesEnabled: boolean;
};

let cached: SmsStatus | undefined;

// Whether texting is set up on the server. Remembered for offline use.
export async function getSmsStatus(): Promise<SmsStatus> {
  if (cached) return cached;
  try {
    const token = localStorage.getItem("token");
    const res = await apiRequest.get("/setup/sms/status", {
      headers: { Authorization: `Bearer ${token}` },
    });
    cached = res.data;
    localStorage.setItem("smsStatus", JSON.stringify(res.data));
    return res.data;
  } catch {
    try {
      return JSON.parse(localStorage.getItem("smsStatus") || "");
    } catch {
      return { enabled: false, repliesEnabled: false };
    }
  }
}
